import React, { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import "react-easy-crop/react-easy-crop.css";
import { X, Check, Loader2, ZoomIn } from "lucide-react";

function createImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", (e) => reject(e));
    img.setAttribute("crossOrigin", "anonymous");
    img.src = url;
  });
}

async function getCroppedBlob(src, pixelCrop) {
  const image = await createImage(src);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(pixelCrop.width);
  canvas.height = Math.round(pixelCrop.height);
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    image,
    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, pixelCrop.width, pixelCrop.height
  );
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92));
}

// Modale de recadrage réutilisable (zoom + déplacement + ratios).
export function ImageCropper({ src, aspect = 1, presets, title = "Recadrer l'image", onCancel, onDone }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [asp, setAsp] = useState(aspect);
  const [pixels, setPixels] = useState(null);
  const [busy, setBusy] = useState(false);
  const onComplete = useCallback((_, p) => setPixels(p), []);
  const confirm = async () => {
    if (!pixels) return;
    setBusy(true);
    try {
      const blob = await getCroppedBlob(src, pixels);
      await onDone(blob);
    } catch (_) {
      onCancel();
    }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-[70] bg-black/85 flex items-center justify-center p-4" data-testid="image-cropper" onClick={onCancel}>
      <div className="bg-[#0d0d10] border border-white/10 w-full max-w-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <h3 className="text-white text-sm font-medium">{title}</h3>
          <button type="button" onClick={onCancel} data-testid="cropper-close" className="text-zinc-400 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
        </div>
        <div className="relative w-full h-[340px] bg-black">
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            aspect={asp}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onComplete}
            restrictPosition={true}
          />
        </div>
        <div className="p-4 space-y-3">
          {presets && presets.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="cropper-presets">
              {presets.map((p) => (
                <button key={p.label} type="button" onClick={() => setAsp(p.value)}
                  className={`text-[11px] uppercase tracking-wider border px-3 py-1.5 transition-colors ${Math.abs(asp - p.value) < 0.001 ? "border-[#C19A6B] text-[#C19A6B] bg-[#C19A6B]/10" : "border-white/15 text-zinc-300 hover:border-white/30"}`}
                  data-testid={`cropper-preset-${p.label}`}>{p.label}</button>
              ))}
            </div>
          )}
          <div className="flex items-center gap-3">
            <ZoomIn className="w-4 h-4 text-[#C19A6B] shrink-0" />
            <input type="range" min={1} max={3} step={0.01} value={zoom} onChange={(e) => setZoom(Number(e.target.value))}
              className="w-full accent-[#C19A6B]" data-testid="cropper-zoom" />
          </div>
          <div className="flex items-center justify-end gap-3 pt-1">
            <button type="button" onClick={onCancel} className="px-4 py-2 text-sm border border-white/15 text-zinc-300 hover:bg-white/5 transition-colors" data-testid="cropper-cancel">Annuler</button>
            <button type="button" onClick={confirm} disabled={busy || !pixels} data-testid="cropper-confirm"
              className="inline-flex items-center gap-2 px-5 py-2 text-sm bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-50 text-white font-medium transition-colors">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Valider
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export const CROP_PRESETS = {
  logo: [{ label: "Carré", value: 1 }, { label: "Paysage", value: 16 / 9 }, { label: "Portrait", value: 3 / 4 }],
  cover: [{ label: "Paysage", value: 16 / 7 }, { label: "Carré", value: 1 }, { label: "Portrait", value: 3 / 4 }],
};

export default ImageCropper;
